import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useEffect, useRef, useState, type FormEvent } from "react";
import type {
  Aoi,
  AoiDetailsInput,
  AoiEditorMode,
  AoiWorldPoint,
} from "@/features/deployment-dashboard/live-view/model/aoi";

import type { AreaLocation } from "@/features/deployment-dashboard/types";
import {
  SECURITY_LANE_ACTIVATION_THRESHOLD,
  type SecurityLaneStatuses,
} from "@/features/deployment-dashboard/live-view/utils/derive-security-lane-states";

export interface LiveViewAreasOfInterestTabProps {
  areas: AreaLocation[];
  displayedAreaId?: string;

  aois: Aoi[];
  selectedAoiId: string | null;
  onToggleAoi: (id: string) => void;
  canModifyAois: boolean;
  /** Whether AOI selection/edit controls (click to select, "..." actions) are interactive. Disabled on non-map views. */
  aoiInteractionsEnabled?: boolean;
  editorMode: AoiEditorMode;
  editingAoiId: string | null;
  draftPoints: AoiWorldPoint[];
  isSavingAoi: boolean;
  aoiError: string | null;
  onStartCreateAoi: () => void;
  onStartEditAoi: (aoi: Aoi) => void;
  onCancelAoiEdit: () => void;
  onUndoAoiPoint: () => void;
  onClearAoiPoints: () => void;
  onDeleteAoiPoint: (pointIndex: number) => void;
  onHoverAoiPoint?: (pointId: string | null) => void;
  hoveredAoiPointIndex: number | null;
  onDeleteAoi: (aoiId: string) => void;
  canDeleteAoi: boolean;
  onSaveAoi: (input: AoiDetailsInput) => void;
  aoiPopulations: Record<string, number>;
  aoiDensities: Record<string, number | null>;
  securityLaneStatuses: SecurityLaneStatuses;
  liveTracksHasFrame?: boolean;
}

/** Area-of-interest list and editing controls for the live-view sidebar. */
export function LiveViewAreasOfInterestTab({
  areas,
  displayedAreaId,
  aois,
  selectedAoiId,
  onToggleAoi,
  canModifyAois,
  aoiInteractionsEnabled = true,
  editorMode,
  editingAoiId,
  draftPoints,
  isSavingAoi,
  aoiError,
  onStartCreateAoi,
  onStartEditAoi,
  onCancelAoiEdit,
  onUndoAoiPoint,
  onClearAoiPoints,
  onDeleteAoiPoint,
  onHoverAoiPoint,
  hoveredAoiPointIndex,
  onDeleteAoi,
  canDeleteAoi,
  onSaveAoi,
  aoiPopulations,
  aoiDensities,
  securityLaneStatuses,
  liveTracksHasFrame = true,
}: LiveViewAreasOfInterestTabProps) {
  const [aoiAreaId, setAoiAreaId] = useState("");
  const [aoiName, setAoiName] = useState("");
  const [aoiDescription, setAoiDescription] = useState("");
  const [aoiBorderColor, setAoiBorderColor] = useState("#2196f3");
  const [pendingDeleteAoi, setPendingDeleteAoi] = useState<Aoi | null>(null);
  const [aoiMenuAnchor, setAoiMenuAnchor] = useState<{ el: HTMLElement; aoi: Aoi } | null>(null);
  const pointItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const editingAoi = aois.find((aoi) => aoi.id === editingAoiId);
  const editingAoiAreaId = editingAoi?.areaId;
  const editingAoiName = editingAoi?.name;
  const editingAoiDescription = editingAoi?.description;
  const editingAoiBorderColor = editingAoi?.borderColor;
  const isEditing = editorMode === "create" || editorMode === "edit";

  const trimmedName = aoiName.trim();
  const trimmedDescription = aoiDescription.trim();
  const draftPointCount = draftPoints.length;
  const canSaveAoi =
    aoiAreaId.length > 0 && trimmedName.length > 0 && draftPointCount >= 3 && !isSavingAoi;
  const customerAois = aois.filter((aoi) => aoi.aoiType === "customer");
  const securityLaneAois = aois
    .filter((aoi) => aoi.aoiType === "security_lane")
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true }));

  useEffect(() => {
    if (editorMode === "edit" && editingAoiName) {
      setAoiAreaId(editingAoiAreaId ?? displayedAreaId ?? "");
      setAoiName(editingAoiName);
      setAoiDescription(editingAoiDescription ?? "");
      setAoiBorderColor(editingAoiBorderColor ?? "#2196f3");
      return;
    }

    setAoiAreaId(editorMode === "create" ? (displayedAreaId ?? "") : "");
    setAoiName("");
    setAoiDescription("");
    setAoiBorderColor("#2196f3");
    setPendingDeleteAoi(null);
  }, [
    editorMode,
    editingAoiId,
    editingAoiAreaId,
    editingAoiName,
    editingAoiDescription,
    editingAoiBorderColor,
    displayedAreaId,
  ]);

  // When a boundary point is hovered on the map (rather than from this list),
  // scroll the corresponding row into view so the highlight is visible even
  // if the point list has been scrolled past it.
  useEffect(() => {
    if (hoveredAoiPointIndex == null) return;
    pointItemRefs.current.get(hoveredAoiPointIndex)?.scrollIntoView({ block: "nearest" });
  }, [hoveredAoiPointIndex]);

  const handleAoiSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSaveAoi) return;

    onSaveAoi({
      areaId: aoiAreaId,
      name: trimmedName,
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
      borderColor: aoiBorderColor,
    });
  };

  const renderAoiRow = (aoi: Aoi) => {
    const areaName = areas.find((area) => area.id === aoi.areaId)?.name ?? "Unknown area";
    const population = aoiPopulations[aoi.id] ?? 0;
    const density = aoiDensities[aoi.id] ?? null;
    const isSelected = selectedAoiId === aoi.id;
    const laneStatus =
      aoi.aoiType === "security_lane" && liveTracksHasFrame
        ? (securityLaneStatuses[aoi.id] ?? {
            state: population > SECURITY_LANE_ACTIVATION_THRESHOLD ? "on" : "off",
            peopleInside: population,
          })
        : undefined;
    const isSecurityLane = aoi.aoiType === "security_lane";
    const isLaneUnavailable = isSecurityLane && !liveTracksHasFrame;
    const laneColor =
      laneStatus?.state === "on"
        ? "success.main"
        : laneStatus?.state === "unavailable" || isLaneUnavailable
          ? "text.secondary"
          : "error.main";

    return (
      <Paper
        key={aoi.id}
        variant="outlined"
        onClick={() => aoiInteractionsEnabled && onToggleAoi(aoi.id)}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          py: 1,
          px: 1.25,
          gap: 0.5,
          borderColor: isSelected ? "primary.main" : "divider",
          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.06) : "transparent",
          cursor: aoiInteractionsEnabled ? "pointer" : "default",
          "&:hover": aoiInteractionsEnabled
            ? {
                backgroundColor: isSelected
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.text.primary, 0.04),
              }
            : undefined,
        })}
        role="button"
        tabIndex={aoiInteractionsEnabled ? 0 : -1}
        aria-label={`${isSelected ? "Deselect" : "Select"} ${aoi.aoiType === "security_lane" ? "security lane" : "area of interest"} ${aoi.name}`}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: isSecurityLane ? laneColor : aoi.borderColor,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
              {aoi.name}
            </Typography>
            <Box
              component="span"
              sx={(theme) => ({
                px: 0.75,
                py: 0.125,
                borderRadius: 0.5,
                fontSize: "0.6875rem",
                fontWeight: 600,
                lineHeight: 1.5,
                color: isSecurityLane ? laneColor : "text.secondary",
                backgroundColor: isSecurityLane
                  ? alpha(
                      laneStatus?.state === "on"
                        ? theme.palette.success.main
                        : laneStatus?.state === "off"
                          ? theme.palette.error.main
                          : theme.palette.text.secondary,
                      0.14
                    )
                  : alpha(theme.palette.text.primary, 0.08),
                whiteSpace: "nowrap",
                flexShrink: 0,
              })}
            >
              {isSecurityLane ? (laneStatus ? laneStatus.state.toUpperCase() : "--") : areaName}
            </Box>
          </Box>
          {!isSecurityLane && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 2 }}>
              {`${population} ${population === 1 ? "person" : "people"}${density != null ? ` · ${density.toFixed(2)} ppl/m²` : ""}`}
            </Typography>
          )}
        </Box>
        {aoi.aoiType === "customer" && (canModifyAois || canDeleteAoi) && (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setAoiMenuAnchor({ el: event.currentTarget, aoi });
            }}
            disabled={!aoiInteractionsEnabled}
            aria-label={`More actions for area of interest ${aoi.name}`}
            sx={{ alignSelf: "center" }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>
    );
  };

  return (
    <>
      <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", p: 2 }}>
        {isEditing ? (
          <Stack component="form" spacing={2} onSubmit={handleAoiSubmit} noValidate>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {editorMode === "create" ? "Create area of interest" : "Edit area of interest"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {editorMode === "create"
                  ? "Click the map to add points. Drag points to move them, or remove individual points below."
                  : "Drag points to move them, click the + on a line to add a point there, or remove individual points below."}
              </Typography>
            </Box>

            {aoiError && <Alert severity="error">{aoiError}</Alert>}

            <TextField
              select
              label="Observation Area"
              value={aoiAreaId}
              onChange={(event) => setAoiAreaId(event.target.value)}
              required
              fullWidth
              size="small"
              disabled={isSavingAoi || areas.length === 0}
              slotProps={{
                select: { inputProps: { "aria-label": "AOI observation area" } },
              }}
            >
              {areas.map((area) => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={aoiName}
              onChange={(event) => setAoiName(event.target.value)}
              required
              fullWidth
              size="small"
              disabled={isSavingAoi}
              slotProps={{ htmlInput: { "aria-label": "Area of interest name" } }}
            />
            <TextField
              label="Description"
              value={aoiDescription}
              onChange={(event) => setAoiDescription(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              size="small"
              disabled={isSavingAoi}
              slotProps={{ htmlInput: { "aria-label": "Area of interest description" } }}
            />
            <TextField
              label="Border colour"
              type="color"
              value={aoiBorderColor}
              onChange={(event) => setAoiBorderColor(event.target.value)}
              fullWidth
              size="small"
              disabled={isSavingAoi}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { "aria-label": "Area of interest border colour" },
              }}
            />

            <Box
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.text.primary, 0.04),
              })}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {draftPointCount} {draftPointCount === 1 ? "point" : "points"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {draftPointCount < 3
                  ? `${3 - draftPointCount} more required`
                  : "Boundary is ready to save"}
              </Typography>
              {draftPoints.length > 0 && (
                <Stack
                  spacing={0.5}
                  sx={{ mt: 1.25, maxHeight: 5 * 32, overflowY: "auto" }}
                  aria-label="AOI boundary points"
                >
                  {draftPoints.map((point, index) => (
                    <Box
                      key={`${index}-${point.x}-${point.y}`}
                      ref={(el: HTMLDivElement | null) => {
                        if (el) {
                          pointItemRefs.current.set(index, el);
                        } else {
                          pointItemRefs.current.delete(index);
                        }
                      }}
                      onMouseEnter={() => onHoverAoiPoint?.(`aoi-draft-point-${index}`)}
                      onMouseLeave={() => onHoverAoiPoint?.(null)}
                      sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        borderRadius: 0.5,
                        px: 0.5,
                        minHeight: 32,
                        backgroundColor:
                          hoveredAoiPointIndex === index
                            ? alpha(theme.palette.warning.main, 0.16)
                            : "transparent",
                      })}
                    >
                      <Typography variant="caption" sx={{ flex: 1 }}>
                        Point {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteAoiPoint(index)}
                        disabled={isSavingAoi}
                        aria-label={`Delete area boundary point ${index + 1}`}
                      >
                        <DeleteOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
              {editorMode === "create" && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={onUndoAoiPoint}
                    disabled={draftPointCount === 0 || isSavingAoi}
                    aria-label="Undo last area boundary point"
                  >
                    Undo
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={onClearAoiPoints}
                    disabled={draftPointCount === 0 || isSavingAoi}
                    aria-label="Clear all area boundary points"
                  >
                    Clear
                  </Button>
                </Stack>
              )}
            </Box>

            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Box sx={{ flex: 1 }} />
              <Button onClick={onCancelAoiEdit} disabled={isSavingAoi}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={!canSaveAoi}>
                {isSavingAoi ? "Saving…" : "Save"}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>
                Areas of Interest
              </Typography>
              {canModifyAois && (
                <Tooltip title="Add area of interest">
                  <span>
                    <IconButton
                      size="small"
                      onClick={onStartCreateAoi}
                      disabled={!aoiInteractionsEnabled}
                      aria-label="Add area of interest"
                      sx={{ border: 1, borderColor: "divider", color: "primary.main" }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            {aoiError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {aoiError}
              </Alert>
            )}

            {aois.length === 0 ? (
              <Box
                sx={(theme) => ({
                  p: 1.5,
                  borderRadius: 1,
                  color: "text.secondary",
                  backgroundColor: alpha(theme.palette.text.primary, 0.04),
                })}
              >
                <Typography variant="body2">No areas of interest found.</Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Box component="section" aria-label="Areas of Interest">
                  {customerAois.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No customer areas of interest found.
                    </Typography>
                  ) : (
                    <List disablePadding aria-label="Customer areas of interest">
                      <Stack spacing={1}>
                        {customerAois.map((aoi) => {
                          return renderAoiRow(aoi);
                        })}
                      </Stack>
                    </List>
                  )}
                </Box>
                {securityLaneAois.length > 0 && (
                  <Box component="section" aria-labelledby="security-lanes-heading">
                    <Typography
                      id="security-lanes-heading"
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 700 }}
                    >
                      Security Lanes
                    </Typography>
                    <List disablePadding aria-label="Security lanes">
                      <Stack spacing={1}>
                        {securityLaneAois.map((aoi) => {
                          return renderAoiRow(aoi);
                        })}
                      </Stack>
                    </List>
                  </Box>
                )}
              </Stack>
            )}
          </>
        )}
      </Box>

      <Menu
        anchorEl={aoiMenuAnchor?.el ?? null}
        open={aoiMenuAnchor !== null}
        onClose={() => setAoiMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canModifyAois && (
          <MenuItem
            onClick={() => {
              if (aoiMenuAnchor) onStartEditAoi(aoiMenuAnchor.aoi);
              setAoiMenuAnchor(null);
            }}
          >
            <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {canDeleteAoi && (
          <MenuItem
            onClick={() => {
              if (aoiMenuAnchor) setPendingDeleteAoi(aoiMenuAnchor.aoi);
              setAoiMenuAnchor(null);
            }}
          >
            <DeleteOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={pendingDeleteAoi !== null}
        onClose={() => setPendingDeleteAoi(null)}
        aria-labelledby="delete-aoi-dialog-title"
      >
        <DialogTitle id="delete-aoi-dialog-title">Delete area of interest?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes {pendingDeleteAoi?.name ?? "this AOI"} and its complete
            boundary.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeleteAoi(null)} disabled={isSavingAoi}>
            Keep
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={isSavingAoi}
            onClick={() => {
              if (!pendingDeleteAoi) return;
              const aoiId = pendingDeleteAoi.id;
              setPendingDeleteAoi(null);
              onDeleteAoi(aoiId);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
